// Profile.jsx — PART 1 (Logic + JSX with Framer Motion)
import React, { useContext, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MoreVertical, User, Edit, Trash2, CheckCircle2 } from "lucide-react";
import { FiEdit, FiLock, FiLogOut, FiGlobe } from "react-icons/fi";
import { SiHashnode } from "react-icons/si";

import {
  FiGithub,
  FiLinkedin,
  FiTwitter,
  FiInstagram,
  FiFacebook,
} from "react-icons/fi";
import {
  FaYoutube,
  FaMedium,
  FaDev,
  FaDribbble,
  FaBehance,
  FaReddit,
  FaSlack,
  FaDiscord,
  FaTelegram,
  FaWhatsapp,
} from "react-icons/fa";

import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../context/UserContext";
import LogoutModal from "../components/modal/LogoutModal";
import { toast } from "react-toastify";

import {
  collection,
  query,
  where,
  getDocs,
  doc,
  deleteDoc,
  getDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { signOut } from "firebase/auth";
import { auth } from "../firebase/firebaseConfig";

/* ---------------- social icon map (same as before) ---------------- */
const socialIcons = {
  github: <FiGithub />,
  linkedin: <FiLinkedin />,
  twitter: <FiTwitter />,
  facebook: <FiFacebook />,
  instagram: <FiInstagram />,
  youtube: <FaYoutube />,
  medium: <FaMedium />,
  devto: <FaDev />,
  hashnode: <SiHashnode />,
  dribbble: <FaDribbble />,
  behance: <FaBehance />,
  stackoverflow: <FiGlobe />,
  reddit: <FaReddit />,
  slack: <FaSlack />,
  discord: <FaDiscord />,
  telegram: <FaTelegram />,
  whatsapp: <FaWhatsapp />,
  portfolio: <FiGlobe />,
  website: <FiGlobe />,
};

/* ---------------- Framer motion variants (subtle professional) ---------------- */
const pageFade = {
  hidden: { opacity: 0, y: 6 },
  enter: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
  exit: { opacity: 0, y: 6, transition: { duration: 0.3 } },
};

const cardRise = {
  hidden: { opacity: 0, y: 10 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.08 * i, duration: 0.5, ease: "easeOut" },
  }),
};

const statPop = {
  hidden: { scale: 0.92, opacity: 0 },
  visible: (i = 0) => ({
    scale: 1,
    opacity: 1,
    transition: { delay: 0.06 * i, duration: 0.45, ease: "backOut" },
  }),
};

/* ------------------------- MAIN COMPONENT (logic + JSX) ------------------------- */
const Profile = () => {
  const { user, logout } = useContext(UserContext);
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("all");
  const [userIssues, setUserIssues] = useState([]);
  const [projects, setProjects] = useState([]);
  const [skills, setSkills] = useState([]);
  const [socials, setSocials] = useState([]);
  const [loading, setLoading] = useState(true);

  const [openMenu, setOpenMenu] = useState(null);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [showSolvedModal, setShowSolvedModal] = useState(false);
  const [solvedId, setSolvedId] = useState(null);

  useEffect(() => {
    if (!user?.id) return;
    fetchUserIssues();
    fetchProjects();
    fetchUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  /* ---------------- fetch issues ---------------- */
  const fetchUserIssues = async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const q = query(
        collection(db, "codeIssues"),
        where("posterId", "==", user.id)
      );
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      data.sort(
        (a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)
      );
      setUserIssues(data);
    } catch (err) {
      console.error("fetchUserIssues:", err);
      toast.error("Failed to load issues");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- fetch projects ---------------- */
  const fetchProjects = async () => {
    if (!user?.id) return;
    try {
      const q = query(collection(db, "users", user.id, "projects"));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      data.sort(
        (a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)
      );
      setProjects(data);
    } catch (err) {
      console.error("fetchProjects:", err);
      toast.error("Failed to load projects");
    }
  };

  /* ---------------- fetch other user data (skills/socials) ---------------- */
  const fetchUserData = async () => {
    if (!user?.id) return;
    try {
      const uDoc = await getDoc(doc(db, "users", user.id));
      if (uDoc.exists()) {
        const d = uDoc.data();
        setSkills(d.skills || []);
        setSocials(d.socials || []);
      }
    } catch (err) {
      console.error("fetchUserData:", err);
    }
  };
  const handleDeleteProject = async (projectId) => {
    if (!user?.id || !projectId) return;
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this project?"
    );
    if (!confirmDelete) return;

    try {
      await deleteDoc(doc(db, "users", user.id, "projects", projectId));
      setProjects((prev) => prev.filter((p) => p.id !== projectId));
      toast.success("🗑️ Project deleted successfully!");
    } catch (error) {
      console.error("Error deleting project:", error);
      toast.error("❌ Failed to delete project.");
    }
  };

  /* ---------------- logout ---------------- */
  const handleLogout = async () => {
    try {
      await signOut(auth);
      logout();
      navigate("/");
      toast.success("Logged out successfully!");
    } catch (err) {
      console.error("logout:", err);
      toast.error("Logout failed");
    }
  };

  /* ---------------- issue actions ---------------- */
  const handleEdit = (id) => navigate("/edit-code-issue/" + id);

  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    setShowDeleteModal(false);
    try {
      await deleteDoc(doc(db, "codeIssues", deleteId));
      toast.success("Issue deleted");
      fetchUserIssues();
    } catch (err) {
      console.error("confirmDelete:", err);
      toast.error("Delete failed");
    } finally {
      setDeleteId(null);
    }
  };

  const handleMarkSolved = (id) => {
    setSolvedId(id);
    setShowSolvedModal(true);
  };

  const confirmSolved = async () => {
    if (!solvedId) return;
    setShowSolvedModal(false);
    try {
      await updateDoc(doc(db, "codeIssues", solvedId), {
        status: "SOLVED",
        solvedAt: serverTimestamp(),
      });
      toast.success("Marked solved");
      fetchUserIssues();
    } catch (err) {
      console.error("confirmSolved:", err);
      toast.error("Update failed");
    } finally {
      setSolvedId(null);
    }
  };

  /* ---------------- filtered issues ---------------- */
  const filteredIssues =
    activeTab === "all"
      ? userIssues
      : userIssues.filter((i) =>
          activeTab === "solved"
            ? i.status === "SOLVED"
            : i.status === "UNSOLVED"
        );

  /* ---------------- small helper to pretty text ---------------- */
  const pretty = (s) => (s ? String(s).replace(/_/g, " ") : "Unknown");

  /* ---------------------------- JSX (animated) ---------------------------- */
  return (
    <motion.div
      variants={pageFade}
      initial="hidden"
      animate="enter"
      exit="exit"
      style={{ minHeight: "100vh" }}
    >
      <Container>
        <InnerMax>
          {/* Top card - animated */}
          <motion.div
            variants={cardRise}
            custom={0}
            initial="hidden"
            animate="visible"
          >
            <TopCard>
              <TopHeader>
                <UserRow>
                  <AvatarWrapper>
                    {user?.profilePic ? (
                      <AvatarImage src={user.profilePic} alt={user.name} />
                    ) : (
                      <User size={48} color="#c59fff" />
                    )}
                  </AvatarWrapper>

                  <div style={{ flex: 1 }}>
                    <h2 style={{ margin: 0 }}>{user?.name}</h2>
                    <p style={{ margin: "6px 0 0 0", color: "#cfcfd6" }}>
                      {user?.email}
                    </p>

                    <ActionsRow>
                      <ActionGradient
                        onClick={() => navigate("/profile/edit/" + user.id)}
                      >
                        Edit Profile <FiEdit />
                      </ActionGradient>
                      <ActionGhost
                        onClick={() => navigate("/profile/edit/password")}
                      >
                        Change Password <FiLock />
                      </ActionGhost>
                      <ActionGradient
                        logout
                        onClick={() => setShowLogoutModal(true)}
                      >
                        Logout <FiLogOut />
                      </ActionGradient>
                    </ActionsRow>
                  </div>
                </UserRow>
              </TopHeader>

              <StatsRow>
                <motion.div variants={statPop} custom={0}>
                  <StatCard>
                    <h3>{userIssues.length}</h3>
                    <span>Total Issues</span>
                  </StatCard>
                </motion.div>

                <motion.div variants={statPop} custom={1}>
                  <StatCard>
                    <h3>
                      {userIssues.filter((i) => i.status === "SOLVED").length}
                    </h3>
                    <span>Solved</span>
                  </StatCard>
                </motion.div>

                <motion.div variants={statPop} custom={2}>
                  <StatCard>
                    <h3>
                      {userIssues.filter((i) => i.status === "UNSOLVED").length}
                    </h3>
                    <span>Unsolved</span>
                  </StatCard>
                </motion.div>
              </StatsRow>
            </TopCard>
          </motion.div>

          {/* Projects */}
          <motion.div
            variants={cardRise}
            custom={1}
            initial="hidden"
            animate="visible"
          >
            <BottomCard>
              <SectionHeader>
                <h3>My Projects</h3>
                <div style={{ display: "flex", gap: 10 }}>
                  <ActionGhost
                    onClick={() => navigate("/profile/edit/projects")}
                  >
                    <FiEdit /> Edit
                  </ActionGhost>
                </div>
              </SectionHeader>

              {projects.length > 0 ? (
                <Grid>
                  {projects.map((p, idx) => (
                    <Card key={p.id || idx}>
                      {p.imageUrl && (
                        <CardImage src={p.imageUrl} alt={p.title} />
                      )}
                      <CardProjectHeader>
                        <h4>{p.title}</h4>
                        <DeleteButton onClick={() => handleDeleteProject(p.id)}>
                          Delete
                        </DeleteButton>
                      </CardProjectHeader>

                      {p.link && (
                        <a href={p.link} target="_blank" rel="noreferrer">
                          {p.link}
                        </a>
                      )}
                      <p>{p.description}</p>
                    </Card>
                  ))}
                </Grid>
              ) : (
                <NoData>No projects added.</NoData>
              )}
            </BottomCard>
          </motion.div>

          {/* Issues */}
          <motion.div
            variants={cardRise}
            custom={2}
            initial="hidden"
            animate="visible"
          >
            <BottomCard>
              <SectionHeader>
                <h3>My Issues</h3>
                <Tabs>
                  <TabButton
                    active={activeTab === "all"}
                    onClick={() => setActiveTab("all")}
                  >
                    All
                  </TabButton>
                  <TabButton
                    active={activeTab === "solved"}
                    onClick={() => setActiveTab("solved")}
                  >
                    Solved
                  </TabButton>
                  <TabButton
                    active={activeTab === "unsolved"}
                    onClick={() => setActiveTab("unsolved")}
                  >
                    Unsolved
                  </TabButton>
                </Tabs>
              </SectionHeader>

              {loading ? (
                <NoData>Loading...</NoData>
              ) : filteredIssues.length > 0 ? (
                <Grid>
                  {filteredIssues.map((issue, idx) => (
                    <Card key={issue.id}>
                      <CardHeader>
                        <div>
                          <h4 style={{ margin: 0 }}>{issue.title}</h4>
                          <SmallMuted>
                            {issue.createdAt?.seconds
                              ? new Date(
                                  issue.createdAt.seconds * 1000
                                ).toLocaleDateString()
                              : ""}
                          </SmallMuted>
                        </div>

                        <div style={{ display: "flex", gap: 8 }}>
                          <MenuBtn onClick={() => handleEdit(issue.id)}>
                            <Edit size={16} />
                          </MenuBtn>
                          <MenuBtn onClick={() => handleDelete(issue.id)}>
                            <Trash2 size={16} />
                          </MenuBtn>
                        </div>
                      </CardHeader>

                      <BadgesRow>
                        <StatusBadge solved={issue.status === "SOLVED"}>
                          {issue.status}
                        </StatusBadge>
                        <TechBadge>{pretty(issue.technology)}</TechBadge>
                        {issue.difficulty && (
                          <DifficultyBadge level={issue.difficulty}>
                            {pretty(issue.difficulty)}
                          </DifficultyBadge>
                        )}
                      </BadgesRow>

                      <p style={{ color: "#ccc" }}>{issue.description}</p>
                    </Card>
                  ))}
                </Grid>
              ) : (
                <NoData>No issues posted.</NoData>
              )}
            </BottomCard>
          </motion.div>

          {/* Skills */}
          <motion.div
            variants={cardRise}
            custom={3}
            initial="hidden"
            animate="visible"
          >
            <BottomCard>
              <SectionHeader>
                <h3>My Skills</h3>
                <ActionGhost onClick={() => navigate("/profile/edit/skills")}>
                  <FiEdit /> Edit
                </ActionGhost>
              </SectionHeader>

              {skills.length > 0 ? (
                <BadgesRow>
                  {skills.map((s) => (
                    <TechBadge key={s}>{s}</TechBadge>
                  ))}
                </BadgesRow>
              ) : (
                <NoData>No skills added.</NoData>
              )}
            </BottomCard>
          </motion.div>

          {/* Socials */}
          <motion.div
            variants={cardRise}
            custom={4}
            initial="hidden"
            animate="visible"
          >
            <BottomCard>
              <SectionHeader>
                <h3>Social Links</h3>
                <ActionGhost onClick={() => navigate("/profile/edit/socials")}>
                  <FiEdit /> Edit
                </ActionGhost>
              </SectionHeader>

              {Array.isArray(socials) && socials.some((s) => s.url) ? (
                <BadgesRow>
                  {socials.map((s, i) =>
                    s.url ? (
                      <SocialLink
                        key={i}
                        href={s.url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {socialIcons[s.name?.toLowerCase()] || <FiGlobe />}{" "}
                        <span style={{ marginLeft: 8 }}>{s.name}</span>
                      </SocialLink>
                    ) : null
                  )}
                </BadgesRow>
              ) : (
                <NoData>No socials added.</NoData>
              )}
            </BottomCard>
          </motion.div>
        </InnerMax>

        {/* Logout modal */}
        <LogoutModal
          isOpen={showLogoutModal}
          onClose={() => setShowLogoutModal(false)}
          onLogout={handleLogout}
        />

        {/* Confirm delete */}
        {showDeleteModal && (
          <ModalOverlay>
            <ModalBox role="dialog" aria-modal="true">
              <h3 style={{ color: "#c59fff" }}>Confirm Delete</h3>
              <p>
                Are you sure you want to delete this issue? This action cannot
                be undone.
              </p>
              <ModalActions>
                <GhostBtn onClick={() => setShowDeleteModal(false)}>
                  Cancel
                </GhostBtn>
                <PrimaryBtn onClick={confirmDelete}>Delete</PrimaryBtn>
              </ModalActions>
            </ModalBox>
          </ModalOverlay>
        )}

        {/* Confirm solved */}
        {showSolvedModal && (
          <ModalOverlay>
            <ModalBox role="dialog" aria-modal="true">
              <h3 style={{ color: "#c59fff" }}>Mark as Solved</h3>
              <p>
                Mark this issue as solved? This will update its status for all
                users.
              </p>
              <ModalActions>
                <GhostBtn onClick={() => setShowSolvedModal(false)}>
                  Cancel
                </GhostBtn>
                <PrimaryBtn onClick={confirmSolved}>Confirm</PrimaryBtn>
              </ModalActions>
            </ModalBox>
          </ModalOverlay>
        )}
      </Container>
    </motion.div>
  );
};

export default Profile;

/* ------------------ End of Part 1 (JSX + Logic) ------------------ */
/* ------------------ STYLED COMPONENTS — PART 2 ------------------ */

/* ---------- layout ---------- */
export const Container = styled.div`
  width: 100%;
  min-height: 100vh;
  background: radial-gradient(circle at top left, #0b0617, #000);
  color: #fff;
  padding: clamp(1.5rem, 4vw, 3rem);
  display: flex;
  justify-content: center;
`;

export const InnerMax = styled.div`
  width: 100%;
  max-width: 1200px;
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

/* ---------- cards ---------- */
export const TopCard = styled.div`
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(155, 92, 255, 0.12);
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
`;

export const BottomCard = styled(TopCard)`
  padding: 1.8rem;
`;

/* ---------- header + user ---------- */
export const TopHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const UserRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;

  h2 {
    font-size: 1.4rem;
    font-weight: 600;
    color: #fff;
  }

  p {
    font-size: 0.9rem;
    color: #cfcfd6;
  }

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;
const CardProjectHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;

  h4 {
    font-size: 18px;
    margin: 0;
    color: #fff;
  }
`;

const DeleteButton = styled.button`
  background: rgba(255, 60, 60, 0.15);
  border: 1px solid rgba(255, 60, 60, 0.4);
  color: #ff6b6b;
  font-size: 13px;
  padding: 6px 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: 0.3s;

  &:hover {
    background: rgba(255, 60, 60, 0.3);
    box-shadow: 0 0 10px rgba(255, 60, 60, 0.4);
  }
`;

export const AvatarWrapper = styled.div`
  width: 90px;
  height: 90px;
  border-radius: 50%;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.08);
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;

  @media (max-width: 480px) {
    width: 70px;
    height: 70px;
  }
`;

export const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

/* ---------- buttons ---------- */
export const ActionsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 0.8rem;
`;

export const ActionGradient = styled.button`
  padding: 0.6rem 1rem;
  border: none;
  border-radius: 10px;
  background: ${({ logout }) =>
    logout
      ? "linear-gradient(90deg,#ef4444,#dc2626)"
      : "linear-gradient(90deg,#9b5cff,#c59fff)"};
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 6px 18px rgba(155, 92, 255, 0.2);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 34px rgba(155, 92, 255, 0.35);
  }
`;

export const ActionGhost = styled(ActionGradient)`
  background: rgba(255, 255, 255, 0.06);
  box-shadow: none;

  &:hover {
    background: linear-gradient(90deg, #9b5cff, #c59fff);
    box-shadow: 0 10px 28px rgba(155, 92, 255, 0.25);
  }
`;

/* ---------- stats ---------- */
export const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 1rem;
  margin-top: 2rem;
`;

export const StatCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  text-align: center;
  padding: 1rem;

  h3 {
    font-size: 1.6rem;
    background: linear-gradient(45deg, #9b5cff, #c59fff);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    margin: 0;
  }

  span {
    color: #aaa;
    font-size: 0.9rem;
  }
`;

/* ---------- sections ---------- */
export const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 1.2rem;

  h3 {
    color: #c59fff;
    font-weight: 600;
  }
`;

/* ---------- grid + card ---------- */
export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1.5rem;
`;

export const Card = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(155, 92, 255, 0.12);
  border-radius: 16px;
  padding: 1rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 36px rgba(155, 92, 255, 0.25);
  }

  h4 {
    color: #fff;
    margin: 0;
  }

  p {
    color: #ccc;
    font-size: 0.9rem;
  }

  a {
    color: #9b5cff;
    font-size: 0.85rem;
    text-decoration: none;
    word-break: break-word;

    &:hover {
      text-decoration: underline;
    }
  }
`;

export const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

export const CardImage = styled.img`
  width: 100%;
  height: 160px;
  border-radius: 10px;
  object-fit: cover;
  margin-bottom: 10px;
`;

/* ---------- tabs ---------- */
export const Tabs = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

export const TabButton = styled.button`
  padding: 8px 16px;
  border-radius: 8px;
  border: none;
  font-weight: 600;
  cursor: pointer;
  color: #fff;
  background: ${({ active }) =>
    active
      ? "linear-gradient(90deg,#9b5cff,#c59fff)"
      : "rgba(255,255,255,0.06)"};
  transition: 0.3s;
  &:hover {
    background: linear-gradient(90deg, #9b5cff, #c59fff);
  }
`;

/* ---------- badges ---------- */
export const BadgesRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0.6rem 0;
`;

export const StatusBadge = styled.span`
  background: ${({ solved }) =>
    solved
      ? "linear-gradient(90deg,#10b981,#059669)"
      : "linear-gradient(90deg,#f59e0b,#b45309)"};
  color: #fff;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.8rem;
`;

export const TechBadge = styled(StatusBadge)`
  background: linear-gradient(90deg, #7b1fa2, #9c27b0);
`;

export const DifficultyBadge = styled(StatusBadge)`
  background: ${({ level }) =>
    level?.toLowerCase() === "high"
      ? "linear-gradient(90deg,#ef4444,#b91c1c)"
      : level?.toLowerCase() === "medium"
      ? "linear-gradient(90deg,#f59e0b,#b45309)"
      : "linear-gradient(90deg,#22c55e,#15803d)"};
`;

/* ---------- small buttons ---------- */
export const MenuBtn = styled.button`
  background: rgba(255, 255, 255, 0.08);
  border: none;
  color: #fff;
  padding: 6px;
  border-radius: 8px;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.18);
  }
`;

/* ---------- socials ---------- */
export const SocialLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 10px;
  padding: 8px 12px;
  color: #fff;
  font-size: 0.9rem;
  transition: 0.3s;

  &:hover {
    background: linear-gradient(90deg, #9b5cff, #c59fff);
    transform: translateY(-2px);
  }
`;

/* ---------- modal ---------- */
export const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
`;

export const ModalBox = styled.div`
  background: rgba(25, 25, 30, 0.95);
  border: 1px solid rgba(155, 92, 255, 0.15);
  border-radius: 16px;
  padding: 1.8rem;
  width: 90%;
  max-width: 380px;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.5);
  text-align: center;

  p {
    color: #ddd;
    font-size: 0.9rem;
  }
`;

export const ModalActions = styled.div`
  margin-top: 1rem;
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
`;

export const GhostBtn = styled.button`
  background: rgba(255, 255, 255, 0.08);
  border: none;
  color: #fff;
  padding: 8px 14px;
  border-radius: 8px;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

export const PrimaryBtn = styled(GhostBtn)`
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  &:hover {
    box-shadow: 0 0 12px rgba(155, 92, 255, 0.4);
  }
`;

/* ---------- misc ---------- */
export const SmallMuted = styled.span`
  font-size: 0.8rem;
  color: #9ca3af;
`;

export const NoData = styled.div`
  text-align: center;
  color: #999;
  font-size: 1rem;
  padding: 1.5rem 0;
`;
